import { faChevronCircleLeft, faChevronCircleRight, faCircleNotch, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionDialog from "components/reusable/ActionDialog";
import ErrorMessage from "components/reusable/ErrorMessage";
import Toolbar from "components/Toolbar";
import AuthContext from "context/AuthContext";
import { Button } from "flowbite-react";
import useToastContext from "hooks/useToastContext";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiDelete, apiGet, apiPost } from "utils/apiFetch";
import pathObj from "utils/apiPath";
import "./MyCards.css";
import NewCard from "./NewCard";

function MyCards() {
  const { t } = useTranslation();
  const notification = useToastContext();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true, defaultValues: {} });

  const [cardsList, setCardsList] = useState(null);
  const cardsScrollRef = useRef(null);
  const [showNavLeft, setShowNavLeft] = useState(false);
  const [showNavRight, setShowNavRight] = useState(false);
  const [cardActiveIndex, setCardActiveIndex] = useState(0);
  const [currentCardContext, setCurrentCardContext] = useState(null);

  const [addMoneyLoading, setAddMoneyLoading] = useState(false);
  const [showDeleteCardWarningDialog, setShowDeleteCardWarningDialog] = useState(false);
  const [showNewCardBottomSheet, setShowNewCardBottomSheet] = useState(false);

  const getMyCards = async () => {
    setCardsList(null);
    try {
      var response = await apiGet(pathObj.myCards);
      if (response.data.success) {
        setCardsList(response.data.results);
      } else {
        setCardsList([]);
        notification.error(response.data.message);
      }
    } catch (err) {}
  };

  useEffect(() => {
    getMyCards();
  }, []);

  const handleScroll = () => {
    const el = cardsScrollRef.current;
    if (!el) return;
    setShowNavLeft(el.scrollLeft > 0);
    setShowNavRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    setCardActiveIndex(idx);
    reset();
    setCurrentCardContext(cardsList[idx] || null);
  };

  useEffect(() => {
    const el = cardsScrollRef.current;
    if (!el) return;
    scrollToCard(0);
    el.removeEventListener("scroll", handleScroll);
    el.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, [cardsList]);

  const scrollToCard = async (i) => {
    if (!cardsScrollRef.current) return;
    const el = cardsScrollRef.current;
    el.scrollTo({
      left: el.clientWidth * i,
      behavior: "smooth",
    });
  };

  const onAddMoneySubmit = async (data) => {
    setAddMoneyLoading(true);
    try {
      const requestBody = {
        saveCard: false,
        cardholderName: data.card_holder,
        cardNumber: data.card_number,
        expirationDate: data.card_expiry,
        amount: data.amount,
        street: user.street || "",
        city: user.city,
        zipCode: user.postalCode || "",
        phone: `+${user.country_code}${user.mobile}`,
        email: user.email,
        country: user.country || "",
        cvv: data.cvv,
        promoCode: data.promoCode,
        message: "",
      };

      const response = await apiPost(pathObj.addMoney, requestBody);

      if (response.data.success) {
        notification.success(response.data.message);
        navigate("/dashboard");
      } else {
        notification.error(response.data.message);
      }
    } catch (err) {}
    setAddMoneyLoading(false);
  };

  const onDeleteCard = async () => {
    if (!currentCardContext) return;

    try {
      const response = await apiDelete(`${pathObj.deleteCard}?id=${currentCardContext._id}`);
      if (response.data.success) {
        getMyCards();
        notification.success(response.data.message);
      } else {
        notification.error(response.data.message);
      }
    } catch (err) {}
  };

  const preventSpace = (e) => {
    e.target.value = e.target.value.trim();
  };

  return (
    <>
      <Toolbar title={t("MY_CARDS")} showBackBtn={true} />
      <div className="flex flex-col w-full justify-center items-center">
        <div className="p-4 rounded-3xl h-full w-full md:w-[25rem]">
          <div className={`relative`}>
            {!cardsList && (
              <div className="flex w-full justify-center">
                <FontAwesomeIcon icon={faCircleNotch} className="text-xl animate-spin h-[15rem]" />
              </div>
            )}
            {cardsList && (
              <div ref={cardsScrollRef} className="rounded-2xl relative flex gap-2 overflow-x-auto no-scrollbar w-full snap-x snap-mandatory" style={{ aspectRatio: "16/9" }}>
                {(cardsList || []).map((it, idx) => {
                  var isVisa = it.card_number.toString().startsWith("4");
                  return (
                    <div key={it._id} className={`snap-center shrink-0 w-full`} style={{ aspectRatio: "16/9", backgroundSize: "cover" }}>
                      <div className={`relative text-white flex h-full w-full ${isVisa ? "bg-visa" : "bg-mastercard"}`}>
                        <span className="font-bold text-2xl font-mono absolute bottom-[7rem] left-[3.5rem] mx-auto">
                          {it.card_number
                            .toString()
                            .match(/.{1,4}/g)
                            .join(" ")}
                        </span>
                        <span className="font-bold text-lg font-mono absolute bottom-[5.5rem] left-[16rem] mx-auto">{it.card_expiry}</span>
                        <span className="font-bold text-lg font-mono absolute bottom-[0.5rem] left-[1rem] mx-auto">{it.card_holder}</span>
                        {isVisa ? (
                          <img src="/images/ic_visa_logo.png" alt="Visa Logo" className="h-[1rem] absolute bottom-[1rem] right-[1rem]" />
                        ) : (
                          <img src="/images/ic_mastercard_logo.png" alt="Mastercard Logo" className="h-[2rem] absolute bottom-[1rem] right-[1rem]" />
                        )}
                      </div>
                    </div>
                  );
                })}
                <NewCard
                  onNewCard={() => {
                    getMyCards();
                  }}
                />
              </div>
            )}
            <FontAwesomeIcon
              icon={faChevronCircleRight}
              onClick={() => {
                cardsScrollRef.current.scrollBy({ left: 500, behavior: "smooth" });
              }}
              className={`cursor-pointer absolute right-[-0.5rem] hidden -translate-y-1/2 card-icon top-1/2 ${showNavRight ? "xl:block" : "xl:hidden"}`}
              size={"2xl"}
            />
            <FontAwesomeIcon
              icon={faChevronCircleLeft}
              onClick={() => {
                cardsScrollRef.current.scrollBy({ left: -500, behavior: "smooth" });
              }}
              className={`cursor-pointer absolute left-[-0.5rem] hidden -translate-y-1/2 card-icon top-1/2 ${showNavLeft ? "xl:block" : "xl:hidden"}`}
              size={"2xl"}
            />
            <div className="flex justify-center gap-2 mt-4 block xl:hidden">
              {Array.from({ length: (cardsList || []).length + 1 }).map((it, idx) => (
                <div
                  key={idx}
                  onClick={() => scrollToCard(idx)}
                  className={`transition-all rounded-full cursor-pointer 
                            ${idx === cardActiveIndex ? "w-6 h-2 bg-primary" : "w-2 h-2 bg-gray-400"}
                          `}
                />
              ))}
            </div>
          </div>
          {currentCardContext && (
            <>
              <Button
                className="btn bg-red-700 w-full rounded-lg mt-5 flex justify-center gap-2 items-center"
                onClick={() => {
                  setShowDeleteCardWarningDialog(true);
                }}
              >
                <FontAwesomeIcon icon={faTrash} className="text-white" />
                <span className="text-lg font-medium text-white">{t("DELETE_CARD")}</span>
              </Button>
              {showDeleteCardWarningDialog && (
                <ActionDialog
                  open={showDeleteCardWarningDialog}
                  title={t("DELETE_CARD")}
                  negativeText={t("BACK")}
                  onPositive={() => {
                    setShowDeleteCardWarningDialog(false);
                    onDeleteCard();
                  }}
                  onNegative={() => {
                    setShowDeleteCardWarningDialog(false);
                  }}
                  onClose={() => {
                    setShowDeleteCardWarningDialog(false);
                  }}
                >
                  <div className="flex justify-center items-center p-4">{t("DELETE_CARD_CONFIRM")}</div>
                </ActionDialog>
              )}

              <div className="font-medium text-lg mt-5 text-center">{t("O_ADD_MONEY")}</div>
              <form onSubmit={handleSubmit(onAddMoneySubmit)}>
                <div className="text-lg font-medium  mt-5 mb-2">{t("AMOUNT")}</div>
                <div className="bg-card p-4 rounded-lg">
                  <div className="flex justify-start gap-3 items-center">
                    <div className="font-medium text-lg text-white">ALL</div>
                    <input
                      type="number"
                      id="amount"
                      inputType="numeric"
                      className="inputform border-0 text-heading text-white font-medium text-sm rounded-base block w-full px-3 rounded-lg py-2.5 shadow-xs placeholder:text-body"
                      placeholder={t("AMOUNT_FIELD")}
                      onKeyDown={(e) => {
                        e.target.value = e.target.value.trim();
                      }}
                      {...register("amount", {
                        required: t("AMOUNT_EMPTY"),
                      })}
                      required
                    />
                  </div>
                </div>
                <input
                  type="text"
                  id="promoCode"
                  className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body mt-3"
                  placeholder={t("PROMO_CODE")}
                  {...register("promoCode", {})}
                />
                <ErrorMessage message={errors?.card_holder?.message} />
                <ErrorMessage message={errors?.amount?.message} />
                <div className="text-lg font-medium mt-5 mb-2">{t("CARD_DETAILS")}</div>
                <input
                  type="text"
                  id="card_holder"
                  value={currentCardContext.card_holder}
                  className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                  placeholder={t("CARD_HOLDER")}
                  {...register("card_holder", {
                    required: t("REGISTER_CARD_HOLDER"),
                  })}
                  disabled
                  required
                />
                <ErrorMessage message={errors?.card_holder?.message} />
                <input
                  type="text"
                  id="card_number"
                  className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body mt-3"
                  value={currentCardContext.card_number}
                  disabled
                  onKeyDown={(e) => {
                    e.target.value = e.target.value.trim();
                  }}
                  {...register("card_number", {})}
                  required
                />
                <ErrorMessage message={errors?.card_number?.message} />
                <div className="grid grid-cols-2 w-full gap-2 mt-3">
                  <div className="col-span-1">
                    <input
                      type="text"
                      id="card_expiry"
                      value={currentCardContext.card_expiry}
                      className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                      disabled
                      {...register("card_expiry", {})}
                    />
                  </div>
                  <div className="col-span-1">
                    <input
                      type="text"
                      id="cvv"
                      className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                      placeholder={t("CVV")}
                      onKeyDown={(e) => preventSpace(e)}
                      {...register("cvv", {
                        required: t("REGISTER_CVV"),
                      })}
                    />
                  </div>
                </div>
                <div className="w-full mt-5">
                  {addMoneyLoading ? (
                    <div className="btn btn_theme w-full flex items-center justify-center p-3">
                      <FontAwesomeIcon icon={faCircleNotch} className="text-white text-xl animate-spin" />
                    </div>
                  ) : (
                    <button className="btn btn_theme w-full text-white" type="submit">
                      {t("O_ADD_MONEY")}
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default MyCards;
